/**
 * Cliente HTTP tipado da API (T-126).
 *
 * Três responsabilidades, e nenhuma a mais: montar a requisição, traduzir
 * `ProblemDetails` em erro tipado, e renovar a sessão uma vez quando o access
 * token expira.
 */

/** Formato de erro da API (docs/10 §1.4, RFC 9457). */
export interface ProblemDetails {
  readonly type?: string;
  readonly title?: string;
  readonly status?: number;
  readonly detail?: string;
  readonly code?: string;
  readonly traceId?: string;
  /** Erros por campo, quando a falha é de validação. */
  readonly errors?: Readonly<Record<string, readonly string[]>>;
}

/**
 * Falha de API já traduzida.
 *
 * Carrega o `problem` inteiro para que o formulário possa marcar campo por
 * campo em vez de exibir uma mensagem solta.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ProblemDetails,
  ) {
    super(problem.detail ?? problem.title ?? 'Não foi possível concluir a operação.');
    this.name = 'ApiError';
  }

  /** Erros do campo informado, se houver. */
  fieldErrors(field: string): readonly string[] {
    return this.problem.errors?.[field] ?? this.problem.errors?.[capitalize(field)] ?? [];
  }

  get isValidation(): boolean {
    return this.status === 400 && this.problem.errors !== undefined;
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/**
 * Token de acesso em memória, nunca em `localStorage`.
 *
 * `localStorage` é legível por qualquer script da página: um XSS levaria o
 * token junto. Em memória, ele morre com a aba — e a sessão é reconstruída pelo
 * cookie `HttpOnly` do refresh, que o script não alcança (docs/12 §A05).
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Chamado quando a sessão morre de vez, para o app voltar ao login. */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
  /** Interno: impede laço infinito de renovação. */
  readonly skipRefresh?: boolean | undefined;
}

export async function request<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const response = await send(path, options);

  // 401 em rota autenticada: tenta renovar UMA vez. O cookie do refresh vai
  // sozinho; o cliente não o enxerga nem precisa.
  if (response.status === 401 && options.skipRefresh !== true && path !== '/auth/refresh') {
    const renewed = await tryRefresh();

    if (renewed) {
      return unwrap<TResponse>(await send(path, { ...options, skipRefresh: true }));
    }

    setAccessToken(null);
    onSessionLost?.();
  }

  return unwrap<TResponse>(response);
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    // Obrigatório para o cookie do refresh viajar. Sem isto, a renovação
    // silenciosamente nunca funciona e o usuário cai no login a cada 15 min.
    credentials: 'include',
  };

  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  if (options.signal !== undefined) {
    init.signal = options.signal;
  }

  return fetch(`${BASE_URL}${path}`, init);
}

async function unwrap<TResponse>(response: Response): Promise<TResponse> {
  if (response.status === 204) {
    return undefined as TResponse;
  }

  const isJson = response.headers.get('content-type')?.includes('json') ?? false;
  const payload: unknown = isJson ? await response.json() : null;

  if (!response.ok) {
    throw new ApiError(response.status, (payload as ProblemDetails | null) ?? {});
  }

  return payload as TResponse;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Renova a sessão, no máximo uma de cada vez.
 *
 * Sem a deduplicação, cinco requisições paralelas recebendo 401 disparariam
 * cinco renovações — e, como o refresh rotaciona, quatro delas apresentariam um
 * token já substituído. O servidor trataria isso como replay e derrubaria a
 * sessão inteira (docs/11 §2.6). O defeito seria intermitente e assustador.
 */
async function tryRefresh(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await send('/auth/refresh', { method: 'POST', skipRefresh: true });

      if (!response.ok) {
        return false;
      }

      const session = (await response.json()) as { accessToken: string };
      setAccessToken(session.accessToken);

      return true;
    } catch {
      return false;
    } finally {
      // Libera na próxima volta do laço de eventos, para que uma falha
      // posterior possa tentar de novo.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

function capitalize(value: string): string {
  return value.length === 0 ? value : (value[0] ?? '').toUpperCase() + value.slice(1);
}
